import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  timelineOf, stageOf, soDate,
  lastReadOf, markThreadRead,
} from "@/store/orders/stages";

/* Order conversation + timeline — the Store↔Sales communication surface.
 * Rides the existing generic chat thread (entity_type "order", entity_id =
 * sales-order id) so both sides read/write the same conversation. Attachments
 * upload through /chat/order/{id}/attachments and render as previews with a
 * fullscreen viewer. Presentation is `.sox-*` (orders.css). */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET = process.env.REACT_APP_BACKEND_URL || "";

/* ── icons ─────────────────────────────────────────────────────────────── */
const s = (children, w = 16) => (
  <svg viewBox="0 0 24 24" width={w} height={w} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
export const SoxIcon = {
  send: (w) => s(<path d="m22 2-7 20-4-9-9-4z M22 2 11 13" />, w),
  clip: (w) => s(<path d="m21.4 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.2-9.19a4 4 0 0 1 5.65 5.65l-9.19 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />, w),
  smile: (w) => s(<><circle cx="12" cy="12" r="9" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></>, w),
  mic: (w) => s(<><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v4" /></>, w),
  search: (w) => s(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></>, w),
  x: (w) => s(<path d="M18 6 6 18M6 6l12 12" />, w),
  download: (w) => s(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>, w),
  expand: (w) => s(<><path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" /></>, w),
  chat: (w) => s(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />, w),
  check: (w) => s(<path d="M20 6 9 17l-5-5" />, w),
  ticks: (w) => s(<><path d="M18 7 8.5 16.5 6 14" /><path d="M22 7l-9.5 9.5" /></>, w),
  cube: (w) => s(<><path d="M21 7.5 12 2 3 7.5v9L12 22l9-5.5z" /><path d="M3 7.5 12 13l9-5.5M12 22V13" /></>, w),
  clock: (w) => s(<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>, w),
  euro: (w) => s(<><path d="M18 7a6.5 6.5 0 1 0 0 10" /><path d="M5 10h7M5 14h7" /></>, w),
  cal: (w) => s(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>, w),
  user: (w) => s(<><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>, w),
  truck: (w) => s(<><rect x="1" y="5" width="14" height="12" rx="1.5" /><path d="M15 9h4l3 4v4h-7" /><circle cx="6.5" cy="17.5" r="1.8" /><circle cx="17.5" cy="17.5" r="1.8" /></>, w),
  upload: (w) => s(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>, w),
  doc: (w) => s(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>, w),
  folder: (w) => s(<path d="M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />, w),
  flag: (w) => s(<><path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.5 2a6 6 0 0 0 3.5-1v11a6 6 0 0 1-3.5 1C13 15 11 13 8 13a6 6 0 0 0-4 1" /></>, w),
};

const SALES_ROLES = new Set(["SALES", "ADMIN", "SUPERADMIN"]);
export const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b >= 1048576) return (b / 1048576).toFixed(1) + " MB";
  if (b >= 1024) return Math.round(b / 1024) + " KB";
  return b + " B";
};
export const attKind = (a) => {
  const t = (a.content_type || "").toLowerCase();
  const n = (a.filename || a.url || "").toLowerCase();
  if (t.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|avif)$/.test(n)) return "image";
  if (t === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  return "doc";
};
const dayLabel = (ts) => {
  const d = new Date(ts); const now = new Date();
  const key = d.toDateString();
  if (key === now.toDateString()) return "Today";
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (key === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
};
const timeLabel = (ts) => {
  try { return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
};
export const initialsOf = (name) => (name || "?").split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase();

/* ── chat meta for order lists (counts, unread, last message, contact) ── */
export function summarizeThread(orderId, msgs, meId) {
  const list = msgs || [];
  const since = lastReadOf(orderId);
  const unread = list.filter((m) => m.author_id !== meId && (!since || String(m.created_at) > since));
  const last = list[list.length - 1] || null;
  const salesMsg = [...list].reverse().find((m) => SALES_ROLES.has(m.author_role));
  const attachments = list.reduce((n, m) => n + ((m.attachments || []).length), 0);
  return {
    count: list.length, unread: unread.length, last,
    lastUnread: unread[unread.length - 1] || null,
    salesContact: salesMsg ? salesMsg.author_name : null,
    attachments,
  };
}

export function useOrdersChatMeta(orders, meId, tick) {
  const [meta, setMeta] = useState({});
  const ids = useMemo(() => (orders || []).slice(0, 40).map((o) => o.id).join(","), [orders]);
  const refresh = useCallback(() => {
    const list = ids ? ids.split(",") : [];
    if (!list.length) { setMeta({}); return; }
    let dead = false;
    Promise.all(list.map((id) =>
      axios.get(`${API}/chat/order/${id}/messages`).then(({ data }) => [id, data || []]).catch(() => [id, []])
    )).then((pairs) => {
      if (dead) return;
      setMeta(Object.fromEntries(pairs.map(([id, msgs]) => [id, summarizeThread(id, msgs, meId)])));
    });
    return () => { dead = true; };
  }, [ids, meId]);
  useEffect(() => { refresh(); }, [refresh, tick]);
  return { meta, refresh };
}

/* ── order timeline ────────────────────────────────────────────────────── */
export function OrderTimeline({ order, compact = false }) {
  const events = timelineOf(order);
  if (!events.length) return <div style={{ fontSize: 12, color: "var(--sox-faint)" }}>No history yet.</div>;
  return (
    <div className="sox-tl" data-testid="order-timeline">
      {events.map((e, i) => {
        const isLast = i === events.length - 1;
        const cls = e.kind === "declined" ? "is-declined" : e.kind === "upload" ? "is-upload" : isLast ? "is-current" : "";
        return (
          <div key={i} className={`sox-tl-item ${e.kind === "upload" ? "is-upload" : ""}`}>
            <div className={`sox-tl-dot ${cls}`}>
              {e.kind === "upload" ? SoxIcon.upload(10) : e.kind === "declined" ? SoxIcon.x(10) : SoxIcon.check(10)}
            </div>
            <div className="sox-tl-body">
              <div className="sox-tl-label">{e.label}</div>
              {e.note && <div className="sox-tl-note">“{e.note}”</div>}
              {!compact && <div className="sox-tl-time">{soDate(e.at)} · {timeLabel(e.at)}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── fullscreen attachment viewer ─────────────────────────────────────── */
export function AttachmentViewer({ att, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const kind = attKind(att);
  const url = att.url && att.url.startsWith("http") ? att.url : ASSET + att.url;
  return (
    <div className="sox-viewer" onClick={onClose} data-testid="attachment-viewer">
      <div className="sox-viewer-top" onClick={(e) => e.stopPropagation()}>
        <span className="sox-viewer-name">{att.filename || "Attachment"}</span>
        {att.size ? <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{fmtSize(att.size)}</span> : null}
        <div className="sox-viewer-act">
          <a className="sox-viewer-btn" href={url} download={att.filename} target="_blank" rel="noreferrer">{SoxIcon.download(14)}Download</a>
          <button className="sox-viewer-btn" onClick={onClose}>{SoxIcon.x(14)}Close</button>
        </div>
      </div>
      <div className="sox-viewer-body" onClick={(e) => e.stopPropagation()}>
        {kind === "image" ? <img src={url} alt={att.filename || ""} /> :
          kind === "pdf" ? <iframe title={att.filename || "PDF"} src={url} /> : (
            <div style={{ color: "#cbd5e1", textAlign: "center", fontSize: 13 }}>
              No inline preview for this file type.<br />
              <a className="sox-viewer-btn" style={{ marginTop: 14, display: "inline-flex" }} href={url} download={att.filename} target="_blank" rel="noreferrer">
                {SoxIcon.download(14)}Download {att.filename}
              </a>
            </div>
          )}
      </div>
    </div>
  );
}

/* ── message attachments ──────────────────────────────────────────────── */
function MessageAttachments({ atts, onOpen }) {
  if (!atts || !atts.length) return null;
  const imgs = atts.filter((a) => attKind(a) === "image");
  const files = atts.filter((a) => attKind(a) !== "image");
  return (
    <>
      {imgs.length > 0 && (
        <div className="sox-att-imgs">
          {imgs.map((a, i) => (
            <button key={i} className="sox-att-img" onClick={(e) => { e.stopPropagation(); onOpen(a); }} title={a.filename}>
              <img src={a.url && a.url.startsWith("http") ? a.url : ASSET + a.url} alt={a.filename || ""} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {files.map((a, i) => (
        <button key={i} className="sox-att-file" onClick={(e) => { e.stopPropagation(); onOpen(a); }}>
          <span className={`sox-att-ic ${attKind(a) === "pdf" ? "" : "t-doc"}`}>{attKind(a) === "pdf" ? "PDF" : "FILE"}</span>
          <span style={{ minWidth: 0 }}>
            <span className="sox-att-name">{a.filename || "file"}</span>
            <span className="sox-att-size" style={{ display: "block" }}>{a.size ? fmtSize(a.size) : "Open preview"}</span>
          </span>
        </button>
      ))}
    </>
  );
}

const EMOJIS = ["👍", "🙏", "🔥", "✅", "❌", "🎉", "😀", "😉", "😅", "🤝", "👀", "💪", "📐", "📎", "🛠️", "🚚", "💶", "⏳", "⚠️", "❤️", "😍", "🤔", "👏", "✨"];

/* ── the chat panel ───────────────────────────────────────────────────── */
export function OrderChatPanel({
  order,                 // sales order (or null → empty state)
  me,                    // auth user
  counterpartName,       // "Sales Team" (store side) / store name (sales side)
  contextRows,           // [{k, v}] project context strip
  online = true,
  lastEvent,             // realtime tick
  onThreadChange,        // (orderId) => void — after read/new messages, lets parents refresh badges
}) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [staged, setStaged] = useState([]);   // uploaded-but-unsent attachments
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dragging, setDragging] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const dragDepth = useRef(0);
  const orderId = order && order.id;
  const orderIdRef = useRef(orderId);
  orderIdRef.current = orderId;

  const load = useCallback(() => {
    const id = orderId;
    if (!id) { setMessages([]); return; }
    axios.get(`${API}/chat/order/${id}/messages`)
      // STALE-RESPONSE GUARD (chat isolation, 2026-07-13): switching to another
      // order/client while this request is in flight must never paint the OLD
      // thread into the NEW conversation — drop the response if the panel moved on.
      .then(({ data }) => { if (orderIdRef.current === id) setMessages(data || []); })
      .catch(() => {});
  }, [orderId]);

  useEffect(() => {
    // Different order (or different client) = a DIFFERENT conversation: blank
    // the thread instantly — the previous client's messages must never be
    // visible while the new thread loads.
    setMessages([]);
    setBody(""); setStaged([]); setQuery(""); setSearchOpen(false);
    load();
  }, [load]);
  useEffect(() => { const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (lastEvent) load(); }, [lastEvent, load]);

  // viewing the thread == reading it
  useEffect(() => {
    if (!orderId || !messages.length) return;
    markThreadRead(orderId);
    onThreadChange && onThreadChange(orderId);
  }, [orderId, messages.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, staged.length, orderId]);

  const uploadFiles = async (files) => {
    if (!orderId || !files || !files.length) return;
    setUploading(true);
    try {
      const done = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await axios.post(`${API}/chat/order/${orderId}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        done.push(data);
      }
      setStaged((sgd) => [...sgd, ...done]);
    } catch { /* toast-free: composer simply keeps what worked */ }
    finally { setUploading(false); }
  };

  const send = async () => {
    const text = body.trim();
    if ((!text && !staged.length) || !orderId || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/order/${orderId}/messages`,
        { body: text || "📎 Attachment", attachments: staged });
      setMessages((m) => [...m, data]);
      setBody(""); setStaged([]); setEmojiOpen(false);
      markThreadRead(orderId);
      onThreadChange && onThreadChange(orderId);
    } catch { /* keep the draft so nothing is lost */ }
    finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const onDrop = (e) => {
    e.preventDefault(); dragDepth.current = 0; setDragging(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  };

  const visible = useMemo(() => {
    if (!query.trim()) return messages;
    const q = query.toLowerCase();
    return messages.filter((m) =>
      (m.body || "").toLowerCase().includes(q) ||
      (m.attachments || []).some((a) => (a.filename || "").toLowerCase().includes(q)) ||
      (m.author_name || "").toLowerCase().includes(q));
  }, [messages, query]);

  // group into day sections
  const groups = useMemo(() => {
    const out = [];
    for (const m of visible) {
      const label = dayLabel(m.created_at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(m);
      else out.push({ label, items: [m] });
    }
    return out;
  }, [visible]);

  const st = order ? stageOf(order.status) : null;

  return (
    <div
      className="sox-panel sox-chat" data-testid="order-chat"
      onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) setDragging(false); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      style={{ position: "relative" }}
    >
      {/* header */}
      <div className="sox-chat-head">
        <div className="sox-chat-av">{initialsOf(counterpartName)}<span className={`st ${online ? "on" : ""}`} /></div>
        <div className="sox-chat-who">
          <div className="sox-chat-name">Chat with {counterpartName}</div>
          <div className="sox-chat-status">{online ? <><span className="dot" />Online</> : "Offline — messages are delivered"}</div>
        </div>
        <div className="sox-chat-hact">
          <button className={`sox-hbtn ${searchOpen ? "on" : ""}`} title="Search messages" onClick={() => { setSearchOpen((v) => !v); setQuery(""); }}>{SoxIcon.search(15)}</button>
          <button className="sox-hbtn" title="Voice notes — coming soon" style={{ cursor: "default", opacity: .55 }}>{SoxIcon.mic(15)}</button>
        </div>
      </div>

      {/* order context — always visible while chatting */}
      {order && contextRows && contextRows.length > 0 && (
        <div className="sox-ctx" data-testid="chat-context">
          {contextRows.map((r, i) => (
            <div key={i} className="sox-ctx-cell">
              <div className="sox-ctx-k">{r.k}</div>
              <div className="sox-ctx-v" title={typeof r.v === "string" ? r.v : undefined}>
                {r.k === "Status" && st
                  ? <span className="sox-badge" style={{ background: st.tone + "22", color: st.tone }}><span className="dot" />{st.label}</span>
                  : r.v}
              </div>
            </div>
          ))}
        </div>
      )}

      {searchOpen && (
        <div className="sox-chat-search">
          {SoxIcon.search(13)}
          <input autoFocus placeholder="Search in conversation…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="chat-search" />
          {query && <button className="sox-hbtn" onClick={() => setQuery("")}>{SoxIcon.x(13)}</button>}
        </div>
      )}

      {/* messages */}
      {!order ? (
        <div className="sox-chat-empty">
          <div className="big">{SoxIcon.chat(24)}</div>
          <b style={{ color: "var(--sox-muted)" }}>No order selected</b>
          Select an order on the left to open its conversation.
        </div>
      ) : (
        <div className="sox-msgs" ref={scrollRef} data-testid="chat-messages">
          {groups.length === 0 && (
            <div className="sox-chat-empty">
              <div className="big">{SoxIcon.chat(24)}</div>
              <b style={{ color: "var(--sox-muted)" }}>{query ? "No matches" : "Start the conversation"}</b>
              {query ? "Try a different search." : `Questions about ${order.number}? Message ${counterpartName} — drawings, PDFs and photos welcome.`}
            </div>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.label}>
              <div className="sox-day"><span>{g.label}</span></div>
              {g.items.map((m) => {
                const mine = m.author_id === (me && me.id);
                const fromSales = SALES_ROLES.has(m.author_role);
                return (
                  <div key={m.id} className={`sox-msg ${mine ? "mine" : ""} ${fromSales ? "from-sales" : ""}`}>
                    {!mine && <div className="sox-msg-av" title={m.author_name}>{initialsOf(m.author_name)}</div>}
                    <div className="sox-msg-stack">
                      {(m.attachments || []).length > 0 && <MessageAttachments atts={m.attachments} onOpen={setViewer} />}
                      {m.body && m.body !== "📎 Attachment" && <div className="sox-bubble">{m.body}</div>}
                      <div className="sox-msg-meta">
                        {!mine && <span className="sox-msg-author">{m.author_name}</span>}
                        <span>{timeLabel(m.created_at)}</span>
                        {mine && <span className="sox-ticks" title="Delivered">{SoxIcon.ticks(12)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {uploading && <div className="sox-typing" title="Uploading…"><i /><i /><i /></div>}
        </div>
      )}

      {/* composer */}
      {order && (
        <div className="sox-compose">
          {staged.length > 0 && (
            <div className="sox-stage-atts">
              {staged.map((a, i) => (
                <span key={i} className="sox-stage-att">
                  {attKind(a) === "image"
                    ? <img src={a.url && a.url.startsWith("http") ? a.url : ASSET + a.url} alt="" />
                    : <span className={`sox-att-ic ${attKind(a) === "pdf" ? "" : "t-doc"}`} style={{ width: 26, height: 26 }}>{attKind(a) === "pdf" ? "PDF" : "F"}</span>}
                  <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                  <button className="x" onClick={() => setStaged((sgd) => sgd.filter((_, j) => j !== i))} title="Remove">{SoxIcon.x(12)}</button>
                </span>
              ))}
            </div>
          )}
          <div className="sox-compose-row">
            <input type="file" multiple style={{ display: "none" }} ref={fileRef}
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            <button className="sox-cbtn" title="Attach files — drawings, PDFs, photos" onClick={() => fileRef.current && fileRef.current.click()} data-testid="chat-attach">{SoxIcon.clip(16)}</button>
            <textarea
              rows={1} placeholder="Type a message…" value={body} data-testid="chat-input"
              onChange={(e) => setBody(e.target.value)} onKeyDown={onKey}
            />
            <button className="sox-cbtn" title="Emoji" onClick={() => setEmojiOpen((v) => !v)}>{SoxIcon.smile(16)}</button>
            <button className="sox-send" disabled={sending || uploading || (!body.trim() && !staged.length)} onClick={send} title="Send" data-testid="chat-send">{SoxIcon.send(15)}</button>
          </div>
          {emojiOpen && (
            <div className="sox-emoji">
              {EMOJIS.map((e) => <button key={e} onClick={() => { setBody((b) => b + e); setEmojiOpen(false); }}>{e}</button>)}
            </div>
          )}
        </div>
      )}

      {dragging && order && <div className="sox-drop">Drop files to attach to {order.number}</div>}
      {viewer && <AttachmentViewer att={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
